import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '@/lib/api-client';

export type PromotionType = 'buy_x_get_y_free' | 'tiered_pricing' | 'discounted_price' | 'buy_x_get_discount';

export interface PromotionScopeDept {
  id: string;
  mode: 'include' | 'exclude';
}

export interface PromotionScopeCat {
  id: string;
  mode: 'include' | 'exclude';
}

export interface PromotionScopeProduct {
  id: string;
  role: 'trigger' | 'reward' | 'applies_to';
  /** Hydrated by the products API so the dashboard can render by name without
   *  preloading the whole catalog. Null when the product was deleted. */
  name?: string | null;
  sku?: string | null;
}

export type BuyXGetDiscountApplyMode = 'once' | 'every' | 'after';

export interface Promotion {
  id: string;
  tenantId: string;
  name: string;
  type: PromotionType;
  config: any;
  /** Null/absent = open-ended on that side (ongoing promotion). */
  startsAt: string | null;
  endsAt: string | null;
  excludeDiscountedProducts: boolean;
  /** False = stopped ("Deactivated"): kept in the list and can be started again. */
  isActive: boolean;
  /** Set once the promotion is deleted; such rows never come back from the list. */
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  locationIds?: string[];
  departments?: PromotionScopeDept[];
  categories?: PromotionScopeCat[];
  products?: PromotionScopeProduct[];
}

export interface PromotionInput {
  name: string;
  type: PromotionType;
  config: any;
  /** Empty string / null = no bound — the API stores NULL. */
  startsAt: string | null;
  endsAt: string | null;
  excludeDiscountedProducts?: boolean;
  locationIds?: string[];
  departments?: PromotionScopeDept[];
  categories?: PromotionScopeCat[];
  products?: PromotionScopeProduct[];
}

export function usePromotions(includeInactive = false) {
  return useQuery({
    queryKey: ['promotions', { includeInactive }],
    queryFn: () =>
      productsApi.get<{ data: Promotion[] }>(`/promotions${includeInactive ? '?includeInactive=true' : ''}`),
  });
}

export function usePromotion(id: string | undefined) {
  return useQuery({
    queryKey: ['promotions', id],
    enabled: !!id,
    queryFn: () => productsApi.get<{ data: Promotion }>(`/promotions/${id}`),
  });
}

export function useCreatePromotion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PromotionInput) => productsApi.post<{ data: Promotion }>('/promotions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

export function useUpdatePromotion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: PromotionInput & { id: string }) =>
      productsApi.put<{ data: Promotion }>(`/promotions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

/** Stop (`false`) or start again (`true`) a promotion without touching anything else. */
export function useSetPromotionActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      productsApi.put<{ data: Promotion }>(`/promotions/${id}`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

/** Soft-deletes: the promotion leaves the list for good (past transactions still reference it). */
export function useDeletePromotion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productsApi.delete(`/promotions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}
